import { fetchEntities, saveOrCreateEntity, deleteEntity } from './entities';
import { schemaOfSchema } from './schemaOfSchema';
import { toStartCase } from 'util/string';
import { memStore } from 'services/storage';

export async function fetchSchemas() {
  const { results: schemas } = await fetchEntities(schemaOfSchema.id);
  updateUserRolesInSchemaOfSchemas(schemas);
  return schemas;
}

function updateUserRolesInSchemaOfSchemas(schemas) {
  const userSchema = schemas.find(schema => schema.name === 'user');
  const rolesProp = (userSchema && userSchema.properties.find(prop => prop.name === 'roles')) || {};
  // add roles for selection of permissions
  schemaOfSchema.$jsonSchema.definitions.userRoles = {
    type: 'array', items: {type: 'string', 'enum': rolesProp.enum, enumNames: rolesProp.enumNames}
  };
}

function canUser(operation, schema, userRoles) {
  const operationRoles = schema && schema.permissions && schema.permissions[operation];
  return !operationRoles || !!(['spacemonk', ...operationRoles].find(role => userRoles.includes(role)));
}

function schemaWithPermissions(userRoles, schema) {
  return {
    schema,
    permissions: ['create', 'update', 'delete'].reduce((permissions, operation) => ({
     ...permissions, [operation]: canUser(operation, schema, userRoles)
    }), {})
  }  
}

export async function fetchAccessibleSchemas() { 
  const userRoles = memStore.get('userRoles');
  let accessibleSchemas = memStore.get('accessibleSchemas');
  if(!accessibleSchemas) {
    const schemas = await fetchSchemas();
    accessibleSchemas = [...schemas, schemaOfSchema]
      .filter(schema => canUser('read', schema, userRoles))
      .map(schema => schemaWithPermissions(userRoles, schema))
    ;    
    memStore.set('accessibleSchemas', accessibleSchemas);
  }
  return accessibleSchemas;
}

// export async function fetchMe(user) {
//   const userSchema = findSchemaOfUserEntities(user);
//   const { results } = await fetchEntities(userSchema.schema.id, `login=${user.userData.phone}`);
//   return results && results[0];
// }

export async function getSchemaFor(schemaId) {
  const accessibleSchemas = await memStore.getOrWaitFor('accessibleSchemas', {defaultValue: []})
  return accessibleSchemas.find(({schema}) => schema.id === schemaId);
}

export async function saveOrCreateSchema(schema) {
  return saveOrCreateEntity(schemaOfSchema.id, schema);
}

export async function deleteSchema(schemaId) {
  return deleteEntity(schemaOfSchema.id, schemaId);
}

export async function toJSONSchema(schema) {
  if(schema.$jsonSchema && /^http:\/\/json-schema.org/.test(schema.$jsonSchema.$schema)) {
    return schema.$jsonSchema;
  }

  const relatedProps = (
    await Promise.all(
      schema.properties
        .filter(prop => prop.relation && prop.relation.schemaId)
        .map(async prop => {
          const {results: entities} = await fetchEntities(prop.relation.schemaId, prop.relation.query) || [];
          return {...prop, 
            enum: (entities || []).map(entity => entity.id),
            enumNames: (entities || []).map(entity => entity.name), // TODO: use schema title
          };
        })
    )
  ).reduce((propsObject, prop) => ({...propsObject, [prop.name]: prop}), {});

  function sanitize(prop) {
    const updatedProp = {
      type: prop.type,
      format: prop.format,
      'default': prop.default,
      'enum': prop['enum'],
      'enumNames': prop['enumNames'],
      readOnly: Boolean(prop.readonly),
      title: prop.title || toStartCase(prop.name), 
      description: prop.description,
    };
    if(prop.type === 'timestamp') {
      return { ...updatedProp, type: 'number'};
    }
    if(prop.type === 'comma-separated-list') {
      const { 'enum': enumValues, enumNames, ...rest} = updatedProp;
      return { ...rest, type: 'array', items: {type: 'string', 'enum': enumValues, enumNames}};
    }
    return updatedProp;
  }
  
  return {
    $schema: "http://json-schema.org/schema#",
    type: "object",
    definitions: schema.definitions,
    properties: schema.properties
      .filter(prop => !prop.hidden)
      .reduce((props, prop) => ({
        ...props, 
        [prop.name]: schema.id === schemaOfSchema.id // TODO make a better fix
          ? prop
          : sanitize(relatedProps[prop.name] || prop)
      }), {}),
    required: schema.properties
      .filter(prop => prop.required)
      .map(prop => prop.name),
  }
}

